import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'vi');

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check for token in localStorage or sessionStorage with CTV prefix
      const token = localStorage.getItem('ctv_token') || sessionStorage.getItem('ctv_token');
      const rememberMe = localStorage.getItem('ctv_rememberMe') === 'true';

      if (token) {
        // If not remember me, check if session storage token exists
        if (!rememberMe && !sessionStorage.getItem('ctv_token')) {
          // Session expired, clear everything
          localStorage.removeItem('ctv_token');
          localStorage.removeItem('ctv_userInfo');
          return;
        }

        // Try to get user info from storage first
        const storedUser = localStorage.getItem('ctv_userInfo') || sessionStorage.getItem('ctv_userInfo');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }

        // Optionally verify token with backend
        try {
          const response = await api.get('/auth/me');
          setUser(response.data.user);
        } catch (apiError) {
          // Token might be expired, try to use stored user
          console.warn('Token verification failed, using stored user data');
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('ctv_token');
      localStorage.removeItem('ctv_userInfo');
      sessionStorage.removeItem('ctv_token');
      sessionStorage.removeItem('ctv_userInfo');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, rememberMe = false) => {
    try {
      const response = await api.post('/auth/login', {
        username,
        password,
        role: 'ctv', // Only CTV can login to this app
      });

      console.log('Login response:', response.data); // Debug log

      const { user } = response.data;
      const accessToken = user?.accessToken; // Token is inside user object

      console.log('Extracted token:', accessToken); // Debug log

      if (!accessToken) {
        throw new Error('No token received from server');
      }

      // Check if user is CTV (userType 201-300)
      if (!user.userType || user.userType < 201 || user.userType > 300) {
        throw new Error('Only CTV can login to this app');
      }

      // Store token based on rememberMe preference
      if (rememberMe) {
        // Store in localStorage for persistent login
        localStorage.setItem('ctv_token', accessToken);
        localStorage.setItem('ctv_userInfo', JSON.stringify(user));
        localStorage.setItem('ctv_rememberMe', 'true');
      } else {
        // Store in sessionStorage for session-only login
        sessionStorage.setItem('ctv_token', accessToken);
        sessionStorage.setItem('ctv_userInfo', JSON.stringify(user));
      }

      setUser(user);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Login failed',
      };
    }
  };

  const logout = async () => {
    console.log('[AuthContext] Logout started');
    try {
      console.log('[AuthContext] Calling logout API');
      await api.post('/auth/logout');
      console.log('[AuthContext] Logout API success');
    } catch (error) {
      console.error('[AuthContext] Logout API error:', error);
    } finally {
      console.log('[AuthContext] Clearing stored data');
      // Clear all stored data
      localStorage.removeItem('ctv_token');
      localStorage.removeItem('ctv_userInfo');
      localStorage.removeItem('ctv_rememberMe');
      localStorage.removeItem('rememberedUsername');
      sessionStorage.removeItem('ctv_token');
      sessionStorage.removeItem('ctv_userInfo');
      setUser(null);

      console.log('[AuthContext] Redirecting to /login');
      // Redirect to login page
      window.location.href = '/login';
    }
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const value = {
    user,
    loading,
    language,
    login,
    logout,
    changeLanguage,
    refreshUser: checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
